import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UserPlus, Upload, Download, Trash2, CheckCircle2, FileText, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NewJoineeTab — 2-step flow:
 *  Step 1: Add a new employee (joinee)
 *  Step 2: Upload all 29 filled forms (one card per Staff-Form column)
 *
 * Columns come from /api/hr/form-columns (the editable canonical list).
 */
const NewJoineeTab = ({ onJoineeSaved }) => {
  const [columns, setColumns] = useState([]);
  const [form, setForm] = useState({ name: '', mobile: '', designation: '', department: '', doj: '' });
  const [saving, setSaving] = useState(false);
  const [savedEmp, setSavedEmp] = useState(null);
  const [uploads, setUploads] = useState({}); // { columnId: filledFormDoc }
  const [uploadingCol, setUploadingCol] = useState(null);
  const [recentJoinees, setRecentJoinees] = useState([]);

  const token = localStorage.getItem('crm_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/hr/form-columns`, { headers })
      .then(res => setColumns(res.data.columns || []))
      .catch(() => setColumns([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const saveJoinee = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/hr/employee-forms/joinee`, {
        name: form.name.trim(),
        mobile: form.mobile.trim() || null,
        designation: form.designation.trim() || null,
        department: form.department.trim() || null,
        doj: form.doj || null,
      }, { headers });
      const emp = res.data.employee;
      setSavedEmp(emp);
      setUploads({});
      setRecentJoinees(prev => [emp, ...prev].slice(0, 5));
      toast.success(`${emp.employeeName} added — now upload their filled forms`);
      if (onJoineeSaved) onJoineeSaved(emp);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add joinee');
    } finally {
      setSaving(false);
    }
  };

  const uploadFilled = async (columnId, file) => {
    if (!file || !savedEmp) return;
    setUploadingCol(columnId);
    try {
      const fd = new FormData();
      fd.append('employee_id', savedEmp.employeeId);
      fd.append('column_id', columnId);
      fd.append('file', file);
      const res = await axios.post(`${API}/hr/employee-forms/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setUploads(prev => ({ ...prev, [columnId]: res.data.form }));
      toast.success('Uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingCol(null);
    }
  };

  const removeFilled = async (filledId, columnId) => {
    try {
      await axios.delete(`${API}/hr/employee-forms/${filledId}`, { headers });
      setUploads(prev => {
        const u = { ...prev };
        delete u[columnId];
        return u;
      });
      toast.success('Removed');
    } catch {
      toast.error('Delete failed');
    }
  };

  const startNew = () => {
    setSavedEmp(null);
    setUploads({});
    setForm({ name: '', mobile: '', designation: '', department: '', doj: '' });
  };

  // ---------- Step 1: Form ----------
  if (!savedEmp) {
    return (
      <div className="space-y-4" data-testid="new-joinee-tab">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <UserPlus className="w-4 h-4 text-amber-600" />
              Add New Joinee
            </CardTitle>
            <p className="text-xs text-stone-500">After saving, you can upload all {columns.length} filled forms for this joinee.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-600 mb-1 block">Full Name *</label>
                <Input data-testid="joinee-name" value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. RAHUL SHARMA" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600 mb-1 block">Mobile</label>
                <Input data-testid="joinee-mobile" value={form.mobile} onChange={e => upd('mobile', e.target.value)} placeholder="10-digit number" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600 mb-1 block">Designation</label>
                <Input data-testid="joinee-designation" value={form.designation} onChange={e => upd('designation', e.target.value)} placeholder="e.g. Junior Engineer" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600 mb-1 block">Department</label>
                <Input data-testid="joinee-department" value={form.department} onChange={e => upd('department', e.target.value)} placeholder="e.g. Marketing" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600 mb-1 block">Date of Joining</label>
                <Input data-testid="joinee-doj" type="date" value={form.doj} onChange={e => upd('doj', e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end mt-4 gap-2">
              <Button
                data-testid="joinee-save-btn"
                disabled={saving || !form.name.trim()}
                onClick={saveJoinee}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {saving ? 'Saving…' : 'Save & Upload Forms →'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {recentJoinees.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Recently Added Joinees (this session)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {recentJoinees.map(j => (
                  <span key={j.employeeId} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                    <CheckCircle2 className="w-3 h-3" /> {j.employeeName}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ---------- Step 2: Upload grid ----------
  const filledCount = Object.keys(uploads).length;
  const totalCount = columns.length;

  return (
    <div className="space-y-4" data-testid="new-joinee-upload-step">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <UserPlus className="w-4 h-4 text-amber-600" />
                {savedEmp.employeeName}
                <span className="text-[10px] bg-amber-200 text-amber-900 rounded px-1.5 py-0.5 font-bold">NEW</span>
              </CardTitle>
              <p className="text-xs text-stone-500 mt-0.5">
                {savedEmp.designation || '—'} · {savedEmp.department || '—'} · DOJ: {savedEmp.doj || '—'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-[10px] uppercase text-stone-500">Forms Uploaded</div>
                <div className="text-base font-bold text-emerald-700">{filledCount} / {totalCount}</div>
              </div>
              <Button
                data-testid="joinee-back-btn"
                variant="outline"
                size="sm"
                onClick={startNew}
                className="text-xs"
              >
                <ChevronLeft className="w-3 h-3 mr-1" /> Add another joinee
              </Button>
            </div>
          </div>
          <div className="w-full bg-stone-100 rounded-full h-2 mt-3">
            <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: totalCount ? `${(filledCount / totalCount) * 100}%` : '0%' }} />
          </div>
        </CardHeader>
        <CardContent>
          {columns.length === 0 ? (
            <div className="text-center py-10 text-stone-400">
              <FileText className="w-10 h-10 mx-auto mb-3 text-stone-300" />
              No form columns set up yet. Configure them in <span className="font-semibold text-emerald-700">Staff Forms Details → Edit Columns</span>.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {columns.map((c, idx) => {
                const f = uploads[c.id];
                const isUp = uploadingCol === c.id;
                return (
                  <div
                    key={c.id}
                    className={`border rounded-lg p-3 transition-all ${f ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-stone-200 hover:border-amber-300'}`}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <div className={`w-7 h-7 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${f ? 'bg-emerald-600 text-white' : 'bg-stone-200 text-stone-600'}`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-stone-800 line-clamp-2 leading-tight" title={c.name}>{c.name}</p>
                        {f && <p className="text-[10px] text-emerald-700 mt-1 truncate">✓ {f.name}</p>}
                      </div>
                    </div>
                    {isUp ? (
                      <div className="text-[10px] text-stone-500 text-center py-1.5">Uploading…</div>
                    ) : f ? (
                      <div className="flex gap-1">
                        <a
                          href={`${API}/hr/employee-forms/${f.id}/download`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-1 py-1 text-[10px] bg-emerald-600 text-white rounded hover:bg-emerald-700"
                        >
                          <Download className="w-3 h-3" /> Get
                        </a>
                        <button
                          onClick={() => removeFilled(f.id, c.id)}
                          className="px-2 py-1 text-[10px] bg-red-50 text-red-600 rounded hover:bg-red-100"
                          data-testid={`joinee-remove-${c.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                        <label className="px-2 py-1 text-[10px] bg-blue-50 text-blue-600 rounded hover:bg-blue-100 cursor-pointer">
                          <Upload className="w-3 h-3" />
                          <input type="file" className="hidden" onChange={e => e.target.files?.[0] && uploadFilled(c.id, e.target.files[0])} />
                        </label>
                      </div>
                    ) : (
                      <label
                        data-testid={`joinee-upload-${c.id}`}
                        className="block text-center py-1.5 border border-dashed border-stone-300 hover:border-amber-400 hover:bg-amber-50/40 rounded cursor-pointer text-[10px] text-stone-500 hover:text-amber-700"
                      >
                        <Upload className="w-3.5 h-3.5 inline mr-1" /> Upload filled form
                        <input
                          type="file"
                          className="hidden"
                          onChange={e => e.target.files?.[0] && uploadFilled(c.id, e.target.files[0])}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewJoineeTab;
